import { z } from 'zod';
import type { User } from '@/types/api';

// Extended User interface for edit page
export interface ExtendedUser extends User {
  phone?: string;
  department?: string;
}

export const roles = [
  'super_admin',
  'admin',
  'manager',
  'auditor',
] as const;

export const statuses = ['Active', 'Inactive'] as const;

// Create User Schema
export const createUserSchema = z
  .object({
    name: z.string().min(2, { message: 'Name must be at least 2 characters.' }),
    email: z.string().email({ message: 'Please enter a valid email.' }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters.' })
      .regex(/\d/, { message: 'Password must contain at least one number.' })
      .regex(/[!@#$%^&*(),.?":{}|<>]/, {
        message: 'Password must contain at least one special character.',
      }),
    confirmPassword: z.string(),
    role: z.enum(['admin', 'manager', 'auditor']), // super_admin excluded - no one can create super_admin users
    property_id: z.coerce.number().optional(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })
  .refine(
    data => {
      // Manager role MUST have a property assigned
      if (data.role === 'manager' && !data.property_id) {
        return false;
      }
      return true;
    },
    {
      message: 'Manager role requires a property assignment',
      path: ['property_id'],
    }
  );

export type CreateUserFormData = z.infer<typeof createUserSchema>;

// Edit User Schema
export const formSchema = z
  .object({
    name: z.string().min(2, { message: 'Name must be at least 2 characters.' }),
    email: z.string().email({ message: 'Please enter a valid email.' }),
    role: z.enum(roles),
    status: z.enum(statuses),
    property_id: z.number().optional().nullable(),
    phone: z.string().optional(),
    department: z.string().optional(),
    newPassword: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .refine(
    data => {
      if (data.newPassword || data.confirmPassword) {
        return data.newPassword === data.confirmPassword;
      }
      return true;
    },
    {
      message: "Passwords don't match",
      path: ['confirmPassword'],
    }
  );

export type EditUserFormData = z.infer<typeof formSchema>;
